import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clearDistrictCache, useDistrict, useDistrictState } from '../district';
import { useAccountAuth } from '../auth';

/**
 * 関係者 (master / 担当地区管理者) のアカウントログイン画面 (地区「強い停止」Phase 1)。
 *
 * 地区が「強い停止」中だと一般ユーザーには {@link NonPublicScreen} しか出ない。関係者はこの画面で
 * email/password ログインすると、custom claim の role で通常フローに入れる。
 *
 * フロー:
 *   1. email/password 入力 → useAccountAuth.signIn
 *   2. role が 'master'、または 'district' かつ districts に当該地区 id を含む → 通常フローへ
 *      - 停止地区の suspended キャッシュを失効させ、再解決させるため `/m/:urlToken` (EntryPage) へ戻す
 *        (DistrictProvider が user 変化 + キャッシュ失効で resolveDistrictByToken を再 fetch → 完全返却)
 *   3. それ以外 (一般アカウント / 担当外地区) → 平易なエラー表示 (内部 key は出さない)
 *
 * スタイルは onboarding の他 page (EntryPage / RecoveryIssuePage) に揃える (max-w-md + Tailwind)。
 */

export function AccountLoginPage() {
  const navigate = useNavigate();
  const { urlToken } = useParams();
  const { signIn, signInWithGoogle } = useAccountAuth();
  const districtState = useDistrictState();
  const district = useDistrict();
  // 当該地区 id。停止中は resolveDistrictByToken の最小返却にも districtId は含まれるが、
  // useDistrict() は status==='ready' のときのみ非 null。停止画面からの遷移では district は null になり得るため
  // districtState から直接 districtId を取り出す (ready / suspended いずれの ready 状態でも参照可能にする)。
  const districtId =
    district?.id ?? (districtState?.status === 'ready' ? districtState.district.districtId : null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setErrorMessage(null);
    if (!email.trim() || !password) {
      setErrorMessage('メールアドレスとパスワードを入力してください。');
      return;
    }
    setSubmitting(true);
    try {
      const { role, districts } = await signIn(email, password);
      const authorized =
        role === 'master' ||
        (role === 'district' && !!districtId && districts.includes(districtId));
      if (!authorized) {
        setErrorMessage(
          'このアカウントには、この地区を利用する権限がありません。担当地区の管理者にご確認ください。',
        );
        setSubmitting(false);
        return;
      }
      // 停止地区の suspended キャッシュを失効させ、再解決で完全返却を受け取れるようにする。
      if (urlToken) clearDistrictCache(urlToken);
      // EntryPage に戻すと DistrictProvider が再解決し、membership 判定後に適切な画面へ振り分ける。
      navigate(`/m/${urlToken}`, { replace: true });
    } catch (err) {
      // useAccountAuth.signIn は既に日本語化済の Error を throw する。
      setErrorMessage(err instanceof Error ? err.message : 'ログインに失敗しました。');
      setSubmitting(false);
    }
  }

  async function handleGoogle() {
    if (submitting) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const result = await signInWithGoogle();
      if (!result) {
        // popup を閉じた等 → 何もしない
        setSubmitting(false);
        return;
      }
      const { role, districts } = result;
      const authorized =
        role === 'master' ||
        (role === 'district' && !!districtId && districts.includes(districtId));
      if (!authorized) {
        setErrorMessage(
          'このアカウントには、この地区を利用する権限がありません。担当地区の管理者にご確認ください。',
        );
        setSubmitting(false);
        return;
      }
      if (urlToken) clearDistrictCache(urlToken);
      navigate(`/m/${urlToken}`, { replace: true });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'ログインに失敗しました。');
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <h1 className="text-2xl font-bold mt-8 mb-2">関係者ログイン</h1>
      <p className="text-sm text-slate-600 mb-6">
        この地区の管理者・運営者の方は、お持ちのメールアドレスとパスワードでログインしてください。
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-slate-800">メールアドレス</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            className="border border-slate-300 rounded-md px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-slate-800">パスワード</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            className="border border-slate-300 rounded-md px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          />
        </label>

        {errorMessage ? (
          <p className="text-sm text-danger-700 bg-danger-50 border border-danger-200 rounded-md px-3 py-2">
            {errorMessage}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-primary-600 text-white py-4 rounded-md font-medium hover:bg-primary-700 disabled:opacity-40"
        >
          {submitting ? 'ログイン中…' : 'ログイン'}
        </button>
      </form>

      <div className="flex items-center gap-3 my-4">
        <span className="h-px flex-1 bg-slate-200" />
        <span className="text-xs text-slate-400">または</span>
        <span className="h-px flex-1 bg-slate-200" />
      </div>
      <button
        type="button"
        onClick={handleGoogle}
        disabled={submitting}
        className="w-full border border-slate-300 bg-white text-slate-800 py-4 rounded-md font-medium hover:bg-slate-50 disabled:opacity-40"
      >
        Google アカウントでログイン
      </button>
    </main>
  );
}
